import type { HttpPort } from "~/lib/http";
import type { StudentToday } from "./studentHome";

export type PraiseReaction = "heart" | "fire" | "clap" | "star" | "muscle";

export interface PraiseMessage {
  id: string;
  message: string;
  created_at: string;
  student_name?: string | null;
  heart_count: number;
  fire_count: number;
  clap_count: number;
  star_count: number;
  muscle_count: number;
  my_likes: PraiseReaction[];
}

export interface StudentFeedbackSnapshot {
  praise: PraiseMessage[];
  today: StudentToday | null;
  isActiveCheckin: boolean;
}

const cleanMessage = (value: string) => value.trim();

export function createStudentFeedbackModule(http: HttpPort, token: string) {
  const validate = (value: string) => {
    const message = cleanMessage(value);
    if (!message) throw new Error("Bitte schreib eine Nachricht.");
    if (message.length > 500) throw new Error("Nachricht ist zu lang (max. 500 Zeichen).");
    return message;
  };
  return {
    async load(classId: string): Promise<StudentFeedbackSnapshot> {
      const [praisePayload, today] = await Promise.all([
        http.request<{ praise?: PraiseMessage[] }>({ path: `/feedback/${classId}/praise`, token }),
        http.request<StudentToday>({ path: `/checkins/${classId}/today`, token })
      ]);
      return { praise: praisePayload.praise ?? [], today, isActiveCheckin: today.has_checked_in === true && today.has_checked_out === false };
    },
    async concern(classId: string, rawMessage: string, anonymous: boolean): Promise<void> {
      await http.request({ path: `/feedback/${classId}/concern`, method: "POST", token, body: { message: validate(rawMessage), is_anonymous: anonymous } });
    },
    async praise(classId: string, rawMessage: string): Promise<void> {
      await http.request({ path: `/feedback/${classId}/praise`, method: "POST", token, body: { message: validate(rawMessage) } });
    },
    async react(classId: string, praiseId: string, reaction: PraiseReaction): Promise<void> {
      await http.request({ path: `/feedback/${classId}/praise/${praiseId}/like`, method: "POST", token, body: { like_type: reaction } });
    }
  };
}

export type StudentFeedbackModule = ReturnType<typeof createStudentFeedbackModule>;
